<script lang="ts">
  import { untrack } from 'svelte';

  import { currentRoute } from './router.svelte';

  import AddContactScreen from '../ui/screens/AddContactScreen.svelte';
  import ContactSettingsScreen from '../ui/screens/ContactSettingsScreen.svelte';
  import ConversationScreen from '../ui/screens/ConversationScreen.svelte';
  import DiagnosticsScreen from '../ui/screens/DiagnosticsScreen.svelte';
  import OnboardingScreen from '../ui/screens/OnboardingScreen.svelte';
  import OrdersScreen from '../ui/screens/OrdersScreen.svelte';
  import ViewScreen from '../ui/screens/ViewScreen.svelte';
  import StaffLoginScreen from '../ui/screens/staff/StaffLoginScreen.svelte';
  import StaffThreadsScreen from '../ui/screens/staff/StaffThreadsScreen.svelte';
  import StaffViewsScreen from '../ui/screens/staff/StaffViewsScreen.svelte';
  import SettingsScreen from '../ui/screens/SettingsScreen.svelte';
  import ThreadsScreen from '../ui/screens/ThreadsScreen.svelte';
  import UpdateToast from '../ui/parts/UpdateToast.svelte';

  // null = never set → onboarding gate. It arrives as a prop because boot.ts
  // already has it: the entry chunk read it from IndexedDB while the app chunk
  // was still downloading. Reading it here instead would mean rendering NOTHING
  // until a second disk round trip finished — and with the splash gone by then,
  // "nothing" is an empty frame in --bg, which in dark mode is near-black. That
  // was one of the two black screens.
  const { initialPhone }: { initialPhone: string | null } = $props();

  const route = $derived(currentRoute());

  // Seeded once and then owned locally: onboarding writes back through `ondone`.
  // `untrack` says that deliberately — the prop is a starting value, not a
  // binding, and boot.ts never re-renders this component with a new one.
  let ownPhone = $state<string | null>(untrack(() => initialPhone));
</script>

<!-- The centered column: full-width on phones, a 600px column on wide screens.
     Deliberately the extent of the "desktop layout" — a decision, not a gap. -->
<div class="frame">
  {#if ownPhone === null}
    <OnboardingScreen ondone={(phone) => (ownPhone = phone)} />
  {:else if route.name === 'threads'}
    <ThreadsScreen />
  {:else if route.name === 'add'}
    <AddContactScreen />
  {:else if route.name === 'conversation'}
    <ConversationScreen accountId={route.accountId} />
  {:else if route.name === 'contact'}
    <ContactSettingsScreen accountId={route.accountId} />
  {:else if route.name === 'orders'}
    <OrdersScreen accountId={route.accountId} />
  {:else if route.name === 'view'}
    <ViewScreen accountId={route.accountId} viewId={route.viewId} />
  {:else if route.name === 'staffLogin'}
    <StaffLoginScreen />
  {:else if route.name === 'staff'}
    <StaffThreadsScreen />
  {:else if route.name === 'staffViews'}
    <StaffViewsScreen threadId={route.threadId} />
  {:else if route.name === 'settings'}
    <SettingsScreen />
  {:else if route.name === 'diagnostics'}
    <DiagnosticsScreen />
  {/if}
</div>
<UpdateToast />

<style>
  .frame {
    max-width: 600px;
    margin: 0 auto;
    /* Exactly the viewport, never more: extra content must scroll inside a
       screen, not grow the page. Two declarations, last supported wins: dvh
       tracks the URL bar, and --app-height comes from viewport.ts when the
       soft keyboard overlays instead of resizing (installed iOS app). */
    height: 100dvh;
    height: var(--app-height, 100dvh);
    overflow: hidden;
    background: var(--bg);
    display: flex;
    flex-direction: column;
  }
  @media (min-width: 720px) {
    .frame {
      border-left: 1px solid var(--line);
      border-right: 1px solid var(--line);
    }
  }
</style>
